import {useLightbox} from 'platform/lightbox';
import {match} from 'ts-pattern';

import {useEffect} from 'react';
import {useLocation} from 'react-router-dom';

import {isNil, isNotNil} from 'ramda';

import {sourcingRoutes} from '@dms/routes';

import {composePath, Nullish, useNavigate} from 'shared/web';

import {rememberSourcingPrevUrl} from '../../../constants/rememberSourcingPrevUrl';
import {SourcingType, useSourcingType} from '../../../hooks/useSourcingType';
import {SourcingTableDispatch} from '../../../store/sourcingTable/reducer';
import {Row} from '../../../types/Row';
import {SourcingTableName} from '../types/SourcingTableName';
import {createRowElementId} from '../utils/createRowElementId';
import {isKeyboardEventFromEditableField} from '../utils/isKeyboardEventFromEditableField';
import {sourcingTableSessionStorage} from '../utils/sourcingTableSessionStorage';

export function useHandleKeyboards(
  selectedRow: Row | Nullish,
  data: Row[],
  tableName: SourcingTableName,
  dispatch: SourcingTableDispatch,
  isEnabled: boolean
) {
  const navigate = useNavigate();
  const sourcingType = useSourcingType();
  const location = useLocation();
  const [, {isOpen: isLightboxOpen}] = useLightbox('sourcingVehicleGallery');

  // useEffect: window keydown subscription. No approved-pattern equivalent.
  useEffect(
    function bindSourcingTableKeyboardNavigation() {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (!isEnabled || isLightboxOpen) {
          return;
        }

        if (isKeyboardEventFromEditableField(e)) {
          return;
        }

        const currentIndex = data.findIndex(
          (row) =>
            isNotNil(selectedRow) && row.sourcingVehicle?.adId === selectedRow.sourcingVehicle?.adId
        );

        match(e.key)
          .with('ArrowDown', () => {
            const row = data[currentIndex + 1] ?? selectedRow;
            if (isNil(row)) {
              return;
            }
            dispatch({type: 'selectRow', row});
            sourcingTableSessionStorage.setLastVisitedVehicleId(
              tableName,
              row.sourcingVehicle.adId
            );
            window.document
              .getElementById(createRowElementId(row))
              ?.scrollIntoView({block: 'nearest'});
          })
          .with('ArrowUp', () => {
            const row = data[currentIndex - 1] ?? selectedRow;
            if (isNil(row)) {
              return;
            }
            dispatch({type: 'selectRow', row});
            sourcingTableSessionStorage.setLastVisitedVehicleId(
              tableName,
              row.sourcingVehicle.adId
            );
            window.document
              .getElementById(createRowElementId(row))
              ?.scrollIntoView({block: 'nearest'});
          })
          .with('ArrowRight', () => {
            if (isNotNil(selectedRow)) {
              dispatch({type: 'expandRow', row: selectedRow, isExpanded: true});
            }
          })
          .with('ArrowLeft', () => {
            if (isNotNil(selectedRow)) {
              dispatch({type: 'expandRow', row: selectedRow, isExpanded: false});
            }
          })
          .with('Enter', () => {
            const adId = selectedRow?.sourcingVehicle?.adId;
            if (isNotNil(adId)) {
              rememberSourcingPrevUrl(location);
              navigate(
                composePath(
                  sourcingType === SourcingType.AUCTIONS
                    ? sourcingRoutes.auctionDetail
                    : sourcingRoutes.vehicleDetail,
                  {params: {adId}}
                )
              );
            }
          });
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    },
    [
      data,
      selectedRow,
      dispatch,
      tableName,
      navigate,
      isLightboxOpen,
      sourcingType,
      location,
      isEnabled,
    ]
  );
}
